import React, { useState, useEffect } from 'react';
import { Tooltip } from 'antd';
import { LuCloudSun, LuCloud, LuCloudFog, LuCloudDrizzle, LuCloudRain, LuCloudSnow, LuCloudRainWind, LuCloudLightning, LuMoonStar, LuSunset, LuSunrise, LuSun } from 'react-icons/lu';

// ================================================================
//  DynamicSunLogo — Real-time solar position for Buenos Aires, AR
//  Drop-in replacement for the static Logo2.png
//
//  Usage:  <DynamicSunLogo size={56} />
//
//  To integrate into Logo.js, replace:
//    import Icon from './Logo2.png';
//    <img src={Icon} className="icon"/>
//  with:
//    import DynamicSunLogo from './DynamicSunLogo';
//    <DynamicSunLogo size={56} />
// ================================================================

const LOCATION = { lat: -34.6037, lon: -58.3816 }; // Buenos Aires
const TIMEZONE = -3; // ART (UTC-3), no DST

const toRad = (d) => d * Math.PI / 180;
const toDeg = (r) => r * 180 / Math.PI;

// SVG layout (viewBox 100x100)
const L = {
    horizonY: 62,
    arcTop: 14,
    arcLeft: 12,
    arcRight: 88,
    sunR: 7,
    glowR: 16,
    moonR: 5.5,
};

// ---------- Solar Calculations ----------

function getDayOfYear(date) {
    const start = new Date(date.getFullYear(), 0, 0);
    return Math.floor((date - start) / 86400000);
}

function getSolarDeclination(N) {
    return -toDeg(Math.asin(0.39779 * Math.cos(toRad(
        0.98565 * (N + 10) + 1.914 * Math.sin(toRad(0.98565 * (N - 2)))
    ))));
}

function getEquationOfTime(N) {
    const B = toRad(360 / 365.24 * (N - 81));
    return 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
}

function getSunriseHA(lat, decl) {
    const cosH = -Math.tan(toRad(lat)) * Math.tan(toRad(decl));
    if (cosH < -1) return 180;
    if (cosH > 1) return 0;
    return toDeg(Math.acos(cosH));
}

function getSolarAltitude(lat, decl, ha) {
    return toDeg(Math.asin(
        Math.sin(toRad(lat)) * Math.sin(toRad(decl)) +
        Math.cos(toRad(lat)) * Math.cos(toRad(decl)) * Math.cos(toRad(ha))
    ));
}

function getSolarData(date) {
    const N = getDayOfYear(date);
    const decl = getSolarDeclination(N);
    const eot = getEquationOfTime(N);
    const tc = eot + 4 * (LOCATION.lon - 15 * TIMEZONE);
    const hours = date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
    const solarTime = hours + tc / 60;
    const ha = 15 * (solarTime - 12);
    const altitude = getSolarAltitude(LOCATION.lat, decl, ha);
    const H0 = getSunriseHA(LOCATION.lat, decl);
    const solarNoon = 12 - tc / 60;
    const sunrise = solarNoon - H0 / 15;
    const sunset = solarNoon + H0 / 15;
    const maxAlt = getSolarAltitude(LOCATION.lat, decl, 0);
    const dayFrac = (sunset - sunrise) > 0 ? (hours - sunrise) / (sunset - sunrise) : 0.5;

    return { altitude, maxAlt, dayFrac, sunrise, sunset, solarNoon };
}

// ---------- Lunar Calculations ----------

const NEW_MOON_REF = new Date(Date.UTC(2000, 0, 6, 18, 14, 0));
const SYNODIC_PERIOD = 29.53058770576;

function getMoonPhaseAngle(date) {
    const daysSince = (date.getTime() - NEW_MOON_REF.getTime()) / 86400000;
    return ((daysSince / SYNODIC_PERIOD) % 1 + 1) % 1;
}

function getMoonIllumination(phase) {
    return (1 - Math.cos(2 * Math.PI * phase)) / 2;
}

function getMoonPhaseName(phase) {
    if (phase < 0.0625) return 'Luna Nueva';
    if (phase < 0.1875) return 'Creciente Cóncava';
    if (phase < 0.3125) return 'Cuarto Creciente';
    if (phase < 0.4375) return 'Creciente Convexa';
    if (phase < 0.5625) return 'Luna Llena';
    if (phase < 0.6875) return 'Menguante Convexa';
    if (phase < 0.8125) return 'Cuarto Menguante';
    if (phase < 0.9375) return 'Menguante Cóncava';
    return 'Luna Nueva';
}

function getMoonPhaseEmoji(phase) {
    if (phase < 0.0625) return '\uD83C\uDF11';
    if (phase < 0.1875) return '\uD83C\uDF12';
    if (phase < 0.3125) return '\uD83C\uDF13';
    if (phase < 0.4375) return '\uD83C\uDF14';
    if (phase < 0.5625) return '\uD83C\uDF15';
    if (phase < 0.6875) return '\uD83C\uDF16';
    if (phase < 0.8125) return '\uD83C\uDF17';
    if (phase < 0.9375) return '\uD83C\uDF18';
    return '\uD83C\uDF11';
}

function getSolarStatusLabel(altitude, weatherCode, dayFrac) {
    if (altitude <= -6) return { Icon: LuMoonStar, label: 'Noche' };
    if (altitude <= 0) {
        if (dayFrac !== undefined && dayFrac < 0.5) return { Icon: LuSunrise, label: 'Amanecer' };
        return { Icon: LuSunset, label: 'Crepúsculo' };
    }
    if (weatherCode === null || weatherCode === undefined) return { Icon: LuSun, label: 'Día' };
    if (weatherCode >= 95) return { Icon: LuCloudLightning, label: 'Tormenta' };
    if (weatherCode >= 80) return { Icon: LuCloudRainWind, label: 'Chaparrón' };
    if (weatherCode >= 71) return { Icon: LuCloudSnow, label: 'Nevada' };
    if (weatherCode >= 61) return { Icon: LuCloudRain, label: 'Lluvia' };
    if (weatherCode >= 51) return { Icon: LuCloudDrizzle, label: 'Llovizna' };
    if (weatherCode >= 45) return { Icon: LuCloudFog, label: 'Niebla' };
    if (weatherCode === 3) return { Icon: LuCloud, label: 'Nublado' };
    if (weatherCode === 2) return { Icon: LuCloudSun, label: 'Parcialmente nublado' };
    if (weatherCode === 1) return { Icon: LuCloudSun, label: 'Mayormente despejado' };
    return { Icon: LuSun, label: 'Despejado' };
}

function getMoonData(date) {
    const phase = getMoonPhaseAngle(date);
    const illumination = getMoonIllumination(phase);
    const solar = getSolarData(date);
    const moonTransitHour = (solar.solarNoon + phase * 24) % 24;
    const moonUpHours = 12.4;
    const moonrise = ((moonTransitHour - moonUpHours / 2) % 24 + 24) % 24;
    const moonset = ((moonTransitHour + moonUpHours / 2) % 24 + 24) % 24;
    const hours = date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
    let hoursSince = hours - moonrise;
    if (hoursSince < -12) hoursSince += 24;
    if (hoursSince > 24) hoursSince -= 24;
    const moonFrac = hoursSince / moonUpHours;
    const maxMoonAlt = 35 + 30 * Math.abs(Math.cos(Math.PI * phase));
    const moonAlt = (moonFrac >= 0 && moonFrac <= 1)
        ? maxMoonAlt * Math.sin(Math.PI * moonFrac) : -10;
    return { phase, illumination, moonAlt, maxMoonAlt, moonFrac };
}

// ---------- Visual Mapping ----------

const WMO_LABELS = {
    0:'Despejado', 1:'Mayormente despejado', 2:'Parcialmente nublado', 3:'Nublado',
    45:'Niebla', 48:'Niebla con escarcha',
    51:'Llovizna leve', 53:'Llovizna moderada', 55:'Llovizna intensa',
    56:'Llovizna helada leve', 57:'Llovizna helada intensa',
    61:'Lluvia leve', 63:'Lluvia moderada', 65:'Lluvia intensa',
    66:'Lluvia helada leve', 67:'Lluvia helada intensa',
    71:'Nevada leve', 73:'Nevada moderada', 75:'Nevada intensa',
    77:'Granizo fino',
    80:'Chaparrón leve', 81:'Chaparrón moderado', 82:'Chaparrón violento',
    85:'Nevada leve intermitente', 86:'Nevada intensa intermitente',
    95:'Tormenta', 96:'Tormenta con granizo leve', 99:'Tormenta con granizo fuerte',
};
function wmoLabel(code) { return WMO_LABELS[code] || `Código ${code}`; }

function mapPosition(solar) {
    const x = L.arcRight - solar.dayFrac * (L.arcRight - L.arcLeft);
    const yRange = L.horizonY - L.arcTop;
    const maxA = Math.max(solar.maxAlt, 1);
    const y = L.horizonY - (solar.altitude / maxA) * yRange;
    return {
        x: Math.max(L.arcLeft - 10, Math.min(L.arcRight + 10, x)),
        y
    };
}

function mapMoonPosition(moon) {
    const x = L.arcRight - Math.max(0, Math.min(1, moon.moonFrac)) * (L.arcRight - L.arcLeft);
    const maxA = Math.max(moon.maxMoonAlt, 1);
    const y = L.horizonY - (moon.moonAlt / maxA) * (L.horizonY - L.arcTop);
    return {
        x: Math.max(L.arcLeft - 10, Math.min(L.arcRight + 10, x)),
        y
    };
}

function lerpHue(h0, h1, t) {
    if (Math.abs(h1 - h0) > 180) {
        if (h1 > h0) h0 += 360; else h1 += 360;
    }
    return ((h0 + (h1 - h0) * t) % 360 + 360) % 360;
}

function glowColor(alt, maxAlt, dayFrac) {
    // Dawn = deep red, noon = bright gold, dusk = vivid violet
    const stops = [
        [0.00,   5, 100, 45],
        [0.08,  15, 100, 50],
        [0.20,  30, 100, 55],
        [0.35,  45, 100, 58],
        [0.50,  48, 100, 62],
        [0.65,  45, 100, 58],
        [0.80, 340, 85,  55],
        [0.92, 300, 80,  50],
        [1.00, 270, 75,  42],
    ];
    const df = Math.max(0, Math.min(1, dayFrac));
    let i = 0;
    while (i < stops.length - 2 && stops[i + 1][0] < df) i++;
    const [f0, h0, s0, l0] = stops[i];
    const [f1, h1, s1, l1] = stops[i + 1];
    const t = f1 > f0 ? (df - f0) / (f1 - f0) : 0;
    const h = lerpHue(h0, h1, t);
    const s = s0 + (s1 - s0) * t;
    const l = l0 + (l1 - l0) * t;
    let alpha;
    if (alt <= -6) alpha = 0;
    else if (alt <= 0) alpha = 0.55 * Math.max(0, (alt + 6) / 6);
    else {
        const r = Math.min(alt / Math.max(maxAlt, 1), 1);
        const edgeness = 1 - 2 * Math.abs(df - 0.5);
        alpha = 0.25 + 0.45 * r + 0.2 * edgeness * (1 - r);
    }
    return `hsla(${Math.round(h)},${Math.round(s)}%,${Math.round(l)}%,${alpha.toFixed(3)})`;
}

// Rays removed — glow-only aesthetic

function getCloudLines(cloudCover) {
    if (!cloudCover || cloudCover <= 0) return [];
    const cover = Math.min(100, Math.max(0, cloudCover));
    const maxLines = 6;
    const numLines = Math.max(1, Math.round(maxLines * cover / 100));
    const baseOpacity = 0.12 + 0.50 * (cover / 100);
    const strokeW = 1.0 + 1.5 * (cover / 100);
    const yMin = 20, yMax = 50;
    const spacing = (yMax - yMin) / (maxLines + 1);
    const colors = ['#999', '#7a9ec2'];
    const lines = [];
    for (let i = 0; i < numLines; i++) {
        const y = yMin + spacing * (i + 1);
        const xL = 10 + (i % 3) * 6;
        const xR = 90 - ((i + 1) % 3) * 5;
        const op = baseOpacity * (0.7 + 0.3 * Math.sin(i * 1.4));
        lines.push({ y, xL, xR, op, strokeW, color: colors[i % 2] });
    }
    return lines;
}

function reflectionOpacity(alt) {
    if (alt > 5) return 0.85;
    if (alt > 0) return 0.4 + 0.45 * (alt / 5);
    if (alt > -6) return 0.15 + 0.25 * ((alt + 6) / 6);
    return 0.12;
}

// ---------- Component ----------

const WEATHER_API_URL = 'https://api.open-meteo.com/v1/forecast?latitude=-34.6037&longitude=-58.3816&current=cloud_cover,temperature_2m,weather_code,relative_humidity_2m&timezone=America/Argentina/Buenos_Aires';

const DynamicSunLogo = ({ size = 56, cloudCover: cloudCoverProp, tooltipSuffix }) => {
    const [now, setNow] = useState(new Date());
    const [tooltipNow, setTooltipNow] = useState(new Date());
    const [apiCloudCover, setApiCloudCover] = useState(0);
    const [apiTemp, setApiTemp] = useState(null);
    const [apiWeatherCode, setApiWeatherCode] = useState(null);
    const [apiHumidity, setApiHumidity] = useState(null);

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 300000); // 5 min
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const tooltipTimer = setInterval(() => setTooltipNow(new Date()), 300000); // 5 min
        return () => clearInterval(tooltipTimer);
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function fetchWeather() {
            try {
                const resp = await fetch(WEATHER_API_URL);
                const data = await resp.json();
                if (!cancelled) {
                    setApiCloudCover(data.current.cloud_cover);
                    setApiTemp(data.current.temperature_2m);
                    setApiWeatherCode(data.current.weather_code);
                    setApiHumidity(data.current.relative_humidity_2m);
                }
            } catch (e) { /* keep last value */ }
        }
        fetchWeather();
        const weatherTimer = setInterval(fetchWeather, 3600000); // 1 hour
        return () => { cancelled = true; clearInterval(weatherTimer); };
    }, []);

    const cc = cloudCoverProp !== undefined ? cloudCoverProp : apiCloudCover;

    const solar = getSolarData(now);
    const moon = getMoonData(now);
    const { x, y } = mapPosition(solar);
    const { altitude, maxAlt, dayFrac } = solar;
    const glow = glowColor(altitude, maxAlt, dayFrac);
    const refOp = reflectionOpacity(altitude);
    const altThreshold = -(L.sunR / (L.horizonY - L.arcTop)) * maxAlt;
    const showSun = altitude > altThreshold;
    const showTwilight = !showSun && altitude > -6;

    // Dynamic glow radius: larger at dawn/dusk
    const edgeness = 1 - 2 * Math.abs(Math.max(0, Math.min(1, dayFrac)) - 0.5);
    const dynamicGlowR = L.glowR + 6 * edgeness * (altitude > 0 && altitude < 20 ? 1 : 0);

    // Moon
    const moonPos = mapMoonPosition(moon);
    const moonAltThresh = -(L.moonR / (L.horizonY - L.arcTop)) * Math.max(moon.maxMoonAlt, 1);
    const showMoon = moon.moonAlt > moonAltThresh && moon.moonFrac >= -0.05 && moon.moonFrac <= 1.05;
    const moonOpacity = altitude > 15 ? 0 : altitude > 5 ? (1 - (altitude - 5) / 10) : 1;
    const k = Math.cos(2 * Math.PI * moon.phase);
    const isWaxing = moon.phase < 0.5;
    const flipX = isWaxing ? -1 : 1;
    const mr = L.moonR;
    const moonGlowAlpha = 0.08 + 0.15 * moon.illumination;

    // Build tooltip (refreshes every 5 min)
    const tipSolar = getSolarData(tooltipNow);
    const tipMoon = getMoonData(tooltipNow);
    const isNight = tipSolar.altitude <= 0;
    const solarStatus = getSolarStatusLabel(tipSolar.altitude, apiWeatherCode, tipSolar.dayFrac);
    const moonName = getMoonPhaseName(tipMoon.phase);
    const StatusIcon = solarStatus.Icon;
    const tooltipContent = (
        <div style={{ fontSize: 13 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 2 }}>
                {isNight
                    ? <><LuMoonStar size={15} style={{ flexShrink: 0 }} /> {moonName}</>
                    : <><StatusIcon size={15} style={{ flexShrink: 0 }} /> {solarStatus.label}</>}
            </div>
            <div>{isNight ? `Alt lunar: ${tipMoon.moonAlt.toFixed(1)}°` : `Alt solar: ${tipSolar.altitude.toFixed(1)}°`}</div>
            <div>Nubosidad: {cc}%</div>
            {apiTemp !== null && <div>Temp: {apiTemp.toFixed(1)}°C</div>}
            {apiHumidity !== null && <div>Humedad: {apiHumidity}%</div>}
            {tooltipSuffix && <div style={{ marginTop: 4, opacity: 0.7, fontStyle: 'italic' }}>{tooltipSuffix}</div>}
        </div>
    );

    const ariaLabel = isNight
        ? `${moonName} — Alt lunar: ${tipMoon.moonAlt.toFixed(1)}°`
        : `${solarStatus.label} — Alt solar: ${tipSolar.altitude.toFixed(1)}°`;

    return (
        <Tooltip title={tooltipContent} placement="right">
        <svg
            viewBox="0 0 100 100"
            width={size}
            height={size}
            style={{ display: 'block', cursor: 'default' }}
            role="img"
            aria-label={ariaLabel}
        >
            <defs>
                <clipPath id="dsl-clip">
                    <rect x="0" y="0" width="100" height={L.horizonY} />
                </clipPath>
            </defs>

            {/* Sun + glow + rays (clipped above horizon) */}
            {showSun && (
                <g clipPath="url(#dsl-clip)">
                    <circle cx={x} cy={y} r={dynamicGlowR} fill={glow} />
                    <circle cx={x} cy={y} r={L.sunR} fill="black" />
                </g>
            )}
            {showTwilight && (
                <g clipPath="url(#dsl-clip)">
                    <circle cx={x} cy={y} r={L.glowR * 1.5} fill={glow} />
                </g>
            )}

            {/* Moon (visible when sun is low/below horizon) */}
            {showMoon && moonOpacity > 0.01 && (
                <g clipPath="url(#dsl-clip)" opacity={moonOpacity}>
                    <circle cx={moonPos.x} cy={moonPos.y} r={mr * 2.2}
                            fill={`hsla(220,30%,75%,${moonGlowAlpha.toFixed(3)})`} />
                    <circle cx={moonPos.x} cy={moonPos.y} r={mr}
                            fill="#e8e8e8" stroke="black" strokeWidth="1.2" />
                    <path
                        d={`M ${moonPos.x} ${moonPos.y - mr}
                            A ${mr} ${mr} 0 0 ${flipX > 0 ? 1 : 0} ${moonPos.x} ${moonPos.y + mr}
                            A ${(Math.abs(k) * mr).toFixed(2)} ${mr} 0 0 ${k * flipX > 0 ? 0 : 1} ${moonPos.x} ${moonPos.y - mr}
                            Z`}
                        fill="#1a1a1a" opacity="0.85"
                    />
                </g>
            )}

            {/* Cloud lines */}
            {cc > 0 && getCloudLines(cc).map((cl, i) => (
                <line key={`cloud-${i}`}
                      x1={cl.xL} y1={cl.y} x2={cl.xR} y2={cl.y}
                      stroke={cl.color} strokeWidth={cl.strokeW} opacity={cl.op} strokeLinecap="round" />
            ))}

            {/* Horizon */}
            <line
                x1="5" y1={L.horizonY} x2="95" y2={L.horizonY}
                stroke="black" strokeWidth="2.5" strokeLinecap="round"
            />

            {/* Reflections */}
            <line x1="30" y1={L.horizonY + 9} x2="70" y2={L.horizonY + 9}
                  stroke="black" strokeWidth="2.2" opacity={refOp} strokeLinecap="round" />
            <line x1="35" y1={L.horizonY + 16} x2="65" y2={L.horizonY + 16}
                  stroke="black" strokeWidth="2" opacity={refOp * 0.7} strokeLinecap="round" />
            <line x1="40" y1={L.horizonY + 23} x2="60" y2={L.horizonY + 23}
                  stroke="black" strokeWidth="1.7" opacity={refOp * 0.45} strokeLinecap="round" />
        </svg>
        </Tooltip>
    );
};

export default DynamicSunLogo;
